import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  Plus,
  ChevronLeft,
  ChevronRight,
  Users,
  UserCircle,
  ChevronDown,
  Tag,
  Upload,
  TriangleAlert,
  CheckCircle2,
  SearchX,
  Briefcase,
  FileSignature,
  MessageSquareText,
  AlertCircle,
  RefreshCw,
} from "lucide-react";
import { clientsApi, contactsApi, PaginatedResponse } from "../../services/api";
import { formatPhoneNumber } from "../../utils/formatPhone";
import DataImportModal from "../ui/DataImportModal";
import PremiumModal from "../ui/PremiumModal";
import SmsOptInCheckbox from "../ui/SmsOptInCheckbox";
import SearchableSelect, { SelectOption } from "../ui/SearchableSelect";
import { errorMessage } from "../ui/Toast";

// ── Types ────────────────────────────────────────────────────────────────────

type TabKey = "all" | "clients" | "contacts";

interface UnifiedPerson {
  _id: string;
  displayName: string;
  kind: "client" | "contact";
  type?: string;
  category?: string;
  emails: { label: string; email: string }[];
  phones: { label: string; number: string }[];
  tags: string[];
}

// ── Constants ────────────────────────────────────────────────────────────────

const tabOptions: { key: TabKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "clients", label: "Clients" },
  { key: "contacts", label: "Contacts" },
];

const categoryOptions = [
  { value: "person", label: "Person" },
  { value: "organization", label: "Organization" },
  { value: "lender", label: "Lender" },
  { value: "realtor", label: "Realtor" },
  { value: "opposingCounsel", label: "Opposing Counsel" },
  { value: "court", label: "Court" },
  { value: "vendor", label: "Vendor" },
  { value: "other", label: "Other" },
];

const formatCategory = (cat: string) =>
  cat === "opposingCounsel"
    ? "Opposing Counsel"
    : cat.charAt(0).toUpperCase() + cat.slice(1);

// ── Component ────────────────────────────────────────────────────────────────

const PeoplePage: React.FC = () => {
  const navigate = useNavigate();

  // ── Tab & Search State ──
  const [activeTab, setActiveTab] = useState<TabKey>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const limit = 25;

  // ── Data State ──
  const [people, setPeople] = useState<UnifiedPerson[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // ── Add New Dropdown ──
  const [addDropdownOpen, setAddDropdownOpen] = useState(false);
  const addDropdownRef = useRef<HTMLDivElement>(null);

  // ── Create Modals ──
  const [showClientModal, setShowClientModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [clientError, setClientError] = useState("");
  const [contactError, setContactError] = useState("");

  // ── Client Create Form ──
  const [clientForm, setClientForm] = useState({
    displayName: "",
    type: "individual" as string,
    email: "",
    phone: "",
    smsOptIn: false,
  });
  const [creatingClient, setCreatingClient] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState<string[]>([]);
  const dupCheckTimer = useRef<ReturnType<typeof setTimeout>>();

  // Check for duplicate client names as user types
  const checkDuplicateName = useCallback((name: string) => {
    clearTimeout(dupCheckTimer.current);
    if (!name.trim() || name.trim().length < 2) {
      setDuplicateWarning([]);
      return;
    }
    dupCheckTimer.current = setTimeout(async () => {
      try {
        const res = await clientsApi.list({ search: name.trim(), limit: 5 });
        const data = (res.data as any).data || res.data || [];
        const matches = data.filter(
          (c: any) => c.displayName.toLowerCase() === name.trim().toLowerCase()
        );
        setDuplicateWarning(matches.map((c: any) => c.displayName));
      } catch {
        setDuplicateWarning([]);
      }
    }, 400);
  }, []);

  // ── Contact Create Form ──
  const [contactForm, setContactForm] = useState({
    displayName: "",
    category: "person" as string,
    email: "",
    phone: "",
    smsOptIn: false,
  });
  const [creatingContact, setCreatingContact] = useState(false);

  // ── Debounced Search ──
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // ── Close dropdown on outside click ──
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (addDropdownRef.current && !addDropdownRef.current.contains(e.target as Node)) {
        setAddDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ── Data Fetching ──
  const fetchPeople = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const params: Record<string, any> = { page, limit };
      if (search) params.search = search;

      if (activeTab === "clients") {
        const res = await clientsApi.list(params);
        const data = res.data as PaginatedResponse<any>;
        const normalized: UnifiedPerson[] = data.data.map((c: any) => ({
          _id: c._id,
          displayName: c.displayName,
          kind: "client" as const,
          type: c.type,
          emails: c.emails || [],
          phones: c.phones || [],
          tags: c.tags || [],
        }));
        setPeople(normalized);
        setTotalCount(data.pagination.total);
        setTotalPages(data.pagination.pages);
      } else if (activeTab === "contacts") {
        const res = await contactsApi.list(params);
        const data = res.data as PaginatedResponse<any>;
        const normalized: UnifiedPerson[] = data.data.map((c: any) => ({
          _id: c._id,
          displayName: c.displayName,
          kind: "contact" as const,
          category: c.category,
          emails: c.emails || [],
          phones: c.phones || [],
          tags: c.tags || [],
        }));
        setPeople(normalized);
        setTotalCount(data.pagination.total);
        setTotalPages(data.pagination.pages);
      } else {
        // "All" tab  -  fetch both in parallel
        const [clientsRes, contactsRes] = await Promise.all([
          clientsApi.list(params),
          contactsApi.list(params),
        ]);

        const clientsData = clientsRes.data as PaginatedResponse<any>;
        const contactsData = contactsRes.data as PaginatedResponse<any>;

        const normalizedClients: UnifiedPerson[] = clientsData.data.map((c: any) => ({
          _id: c._id,
          displayName: c.displayName,
          kind: "client" as const,
          type: c.type,
          emails: c.emails || [],
          phones: c.phones || [],
          tags: c.tags || [],
        }));

        const normalizedContacts: UnifiedPerson[] = contactsData.data.map((c: any) => ({
          _id: c._id,
          displayName: c.displayName,
          kind: "contact" as const,
          category: c.category,
          emails: c.emails || [],
          phones: c.phones || [],
          tags: c.tags || [],
        }));

        const merged = [...normalizedClients, ...normalizedContacts].sort((a, b) =>
          a.displayName.localeCompare(b.displayName)
        );

        setPeople(merged);
        setTotalCount(clientsData.pagination.total + contactsData.pagination.total);
        setTotalPages(Math.max(clientsData.pagination.pages, contactsData.pagination.pages));
      }
    } catch (err) {
      console.error("Failed to fetch people:", err);
      setLoadError(errorMessage(err, "Couldn't load your clients and contacts."));
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, limit, search]);

  useEffect(() => {
    fetchPeople();
  }, [fetchPeople]);

  // ── Tab Change ──
  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setPage(1);
  };

  // ── Create Client Handler ──
  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingClient(true);
    setClientError("");
    try {
      await clientsApi.create({
        displayName: clientForm.displayName,
        type: clientForm.type,
        emails: clientForm.email ? [{ label: "main", email: clientForm.email }] : [],
        phones: clientForm.phone ? [{ label: "main", number: clientForm.phone }] : [],
        smsOptIn: clientForm.smsOptIn,
      });
      setShowClientModal(false);
      setClientForm({ displayName: "", type: "individual", email: "", phone: "", smsOptIn: false });
      setDuplicateWarning([]);
      fetchPeople();
    } catch (err) {
      console.error("Failed to create client:", err);
      setClientError(errorMessage(err, "Couldn't create the client. Please try again."));
    } finally {
      setCreatingClient(false);
    }
  };

  // ── Create Contact Handler ──
  const handleCreateContact = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingContact(true);
    setContactError("");
    try {
      await contactsApi.create({
        displayName: contactForm.displayName,
        category: contactForm.category,
        emails: contactForm.email ? [{ label: "main", email: contactForm.email }] : [],
        phones: contactForm.phone ? [{ label: "main", number: contactForm.phone }] : [],
        smsOptIn: contactForm.smsOptIn,
      });
      setShowContactModal(false);
      setContactForm({ displayName: "", category: "person", email: "", phone: "", smsOptIn: false });
      fetchPeople();
    } catch (err) {
      console.error("Failed to create contact:", err);
      setContactError(errorMessage(err, "Couldn't create the contact. Please try again."));
    } finally {
      setCreatingContact(false);
    }
  };

  // ── Helpers ──
  const personPath = (person: UnifiedPerson) =>
    person.kind === "client" ? `/clients/${person._id}` : `/contacts/${person._id}`;

  const getInitials = (name: string) =>
    name
      .split(" ")
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("");

  const getTypeCategoryLabel = (person: UnifiedPerson) => {
    if (person.kind === "client") {
      return person.type ? person.type.charAt(0).toUpperCase() + person.type.slice(1) : "Client";
    }
    return person.category ? formatCategory(person.category) : "Contact";
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div
            className="h-10 w-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: "var(--color-primary)" }}
          >
            <Users className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Clients</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {totalCount} {totalCount === 1 ? "record" : "records"} in your directory
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Import Button */}
          <button
            onClick={() => setShowImportModal(true)}
            className="inline-flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            <Upload className="h-4 w-4" />
            Import
          </button>

          {/* Add New Dropdown */}
          <div className="relative" ref={addDropdownRef}>
            <button
              onClick={() => setAddDropdownOpen(!addDropdownOpen)}
              className="inline-flex items-center gap-2 px-5 py-3 text-white font-bold rounded-xl transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              <Plus className="h-5 w-5" />
              Add New
              <ChevronDown className="h-4 w-4" />
            </button>

          <AnimatePresence>
            {addDropdownOpen && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-20"
              >
                <button
                  onClick={() => {
                    setAddDropdownOpen(false);
                    setShowClientModal(true);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <Users className="h-4 w-4 text-brand-accent" />
                  New Client
                </button>
                <button
                  onClick={() => {
                    setAddDropdownOpen(false);
                    setShowContactModal(true);
                  }}
                  className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
                >
                  <UserCircle className="h-4 w-4 text-blue-600" />
                  New Contact
                </button>
              </motion.div>
            )}
          </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-1.5 mb-6 inline-flex gap-1">
        {tabOptions.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleTabChange(tab.key)}
            className="relative flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
            style={{
              color: activeTab === tab.key ? "#ffffff" : "#64748b",
            }}
          >
            {activeTab === tab.key && (
              <motion.div
                layoutId="people-tab-pill"
                className="absolute inset-0 rounded-lg"
                style={{ backgroundColor: "var(--color-primary)" }}
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            <span className="relative z-10">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search clients & contacts..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-emerald-600 transition-colors"
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
          </div>
        ) : loadError ? (
          <div className="flex flex-col items-center justify-center py-20 px-6">
            <div className="p-4 rounded-2xl bg-red-50 border border-red-100 mb-4">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 mb-1">Something went wrong</h3>
            <p className="text-sm text-slate-500 text-center max-w-md mb-5">{loadError}</p>
            <button
              onClick={fetchPeople}
              className="inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-bold rounded-xl transition-colors"
              style={{ backgroundColor: "var(--color-primary)" }}
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          </div>
        ) : people.length === 0 ? (
          search ? (
            /* No search results */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-slate-100 to-slate-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-5 rounded-3xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 shadow-sm">
                  <SearchX className="h-10 w-10 text-slate-400" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">No results found</h3>
              <p className="text-sm text-slate-500 text-center max-w-md leading-relaxed">
                We couldn't find anyone matching "{search}". Try adjusting your search terms or clearing your filters.
              </p>
            </motion.div>
          ) : (
            /* True empty state — no clients at all */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="relative mb-6">
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-emerald-100 to-emerald-50 blur-xl scale-150 opacity-60" />
                <div className="relative p-5 rounded-3xl bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 shadow-sm">
                  <Users className="h-10 w-10" style={{ color: "var(--color-primary)" }} />
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Your client list is empty</h3>
              <p className="text-sm text-slate-500 text-center max-w-md mb-6 leading-relaxed">
                Add your first client to start managing matters, billing, and communications.
                Everything you need in one place.
              </p>
              <button
                onClick={() => setShowClientModal(true)}
                className="inline-flex items-center gap-2 px-6 py-3 text-white font-bold rounded-xl transition-all shadow-md hover:shadow-lg active:scale-[0.98]"
                style={{ backgroundColor: "var(--color-primary)" }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
              >
                <Plus className="h-5 w-5" />
                Add Your First Client
              </button>
              <div className="mt-8 flex flex-wrap justify-center gap-4 max-w-lg">
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  Track matters & billing
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  Send documents for e-sign
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-400">
                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                  Automated communications
                </div>
              </div>
            </motion.div>
          )
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200" style={{ backgroundColor: "var(--color-primary)" }}>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                      Name
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider">
                      Type / Category
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden md:table-cell">
                      Email
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden lg:table-cell">
                      Phone
                    </th>
                    <th className="text-left px-6 py-3 text-xs font-semibold text-white uppercase tracking-wider hidden xl:table-cell">
                      Tags
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {people.map((person, i) => (
                    <motion.tr
                      key={`${person.kind}-${person._id}`}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: i * 0.03 }}
                      onClick={() => navigate(personPath(person))}
                      className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                    >
                      {/* Name with initials avatar */}
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div
                            className="h-9 w-9 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0"
                            style={{
                              backgroundColor: person.kind === "client" ? "var(--color-accent)" : "#6366f1",
                            }}
                          >
                            {getInitials(person.displayName)}
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(personPath(person));
                              }}
                              className="font-medium text-sm text-slate-800 text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 rounded"
                            >
                              {person.displayName}
                            </button>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                                person.kind === "client"
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-blue-100 text-blue-700"
                              }`}
                            >
                              {person.kind}
                            </span>
                          </div>
                        </div>
                      </td>

                      {/* Type / Category */}
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
                            person.kind === "client"
                              ? "bg-emerald-100 text-emerald-700"
                              : "bg-blue-100 text-blue-700"
                          }`}
                        >
                          {getTypeCategoryLabel(person)}
                        </span>
                      </td>

                      {/* Email */}
                      <td className="px-6 py-4 text-sm text-slate-600 hidden md:table-cell">
                        {person.emails?.[0]?.email || "-"}
                      </td>

                      {/* Phone */}
                      <td className="px-6 py-4 text-sm text-slate-600 hidden lg:table-cell">
                        {person.phones?.[0]?.number ? formatPhoneNumber(person.phones[0].number) : "-"}
                      </td>

                      {/* Tags */}
                      <td className="px-6 py-4 hidden xl:table-cell">
                        <div className="flex flex-wrap gap-1">
                          {(person.tags || []).slice(0, 3).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-600 rounded text-xs"
                            >
                              <Tag className="h-3 w-3" />
                              {tag}
                            </span>
                          ))}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Page {page} of {totalPages} ({totalCount} total)
                </p>
                <div className="flex gap-2">
                  <button
                    disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="p-2 rounded-lg border border-slate-200 disabled:opacity-40 hover:bg-slate-50 transition-colors"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Create Client Modal ── */}
      <PremiumModal
        open={showClientModal}
        onClose={() => {
          setShowClientModal(false);
          setDuplicateWarning([]);
          setClientError("");
        }}
        title="New Client"
        subtitle="Add a new client to your firm"
        icon={Users}
      >
        <form onSubmit={handleCreateClient} className="space-y-4">
          {clientError && (
            <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{clientError}</span>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Display Name *
            </label>
            <input
              type="text"
              required
              value={clientForm.displayName}
              onChange={(e) => {
                setClientForm({ ...clientForm, displayName: e.target.value });
                checkDuplicateName(e.target.value);
              }}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="John Smith or Acme Corp"
            />
            {duplicateWarning.length > 0 ? (
              <div className="flex items-start gap-2 mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                <TriangleAlert className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-700">
                  A client named <span className="font-semibold">"{duplicateWarning[0]}"</span> already exists.
                  You can still create this client, but make sure it's not a duplicate.
                </p>
              </div>
            ) : (
              <p className="text-xs text-slate-400 mt-1">How this client appears across the system</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Type</label>
            <SearchableSelect
              options={[
                { value: "individual", label: "Individual" },
                { value: "organization", label: "Organization" },
                { value: "estate", label: "Estate" },
              ]}
              value={clientForm.type}
              onChange={(val) => setClientForm({ ...clientForm, type: val || "individual" })}
              placeholder="Select type"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={clientForm.email}
              onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="client@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="tel"
              value={clientForm.phone}
              onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="(555) 123-4567"
            />
            <SmsOptInCheckbox
              checked={clientForm.smsOptIn}
              onChange={(val) => setClientForm({ ...clientForm, smsOptIn: val })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowClientModal(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingClient || !clientForm.displayName}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              {creatingClient ? "Creating..." : "Create Client"}
            </button>
          </div>
        </form>
      </PremiumModal>

      {/* ── Create Contact Modal ── */}
      <PremiumModal
        open={showContactModal}
        onClose={() => {
          setShowContactModal(false);
          setContactError("");
        }}
        title="New Contact"
        subtitle="Add a new contact to your directory"
        icon={UserCircle}
      >
        <form onSubmit={handleCreateContact} className="space-y-4">
          {contactError && (
            <div className="flex items-start gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{contactError}</span>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Display Name *
            </label>
            <input
              type="text"
              required
              value={contactForm.displayName}
              onChange={(e) => setContactForm({ ...contactForm, displayName: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="Jane Doe or ABC Lending"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Category</label>
            <SearchableSelect
              options={categoryOptions.map((opt) => ({
                value: opt.value,
                label: opt.label,
              }))}
              value={contactForm.category}
              onChange={(val) => setContactForm({ ...contactForm, category: val || "person" })}
              placeholder="Select category"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={contactForm.email}
              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="contact@email.com"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
            <input
              type="tel"
              value={contactForm.phone}
              onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
              className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:border-emerald-600"
              placeholder="(555) 123-4567"
            />
            <SmsOptInCheckbox
              checked={contactForm.smsOptIn}
              onChange={(val) => setContactForm({ ...contactForm, smsOptIn: val })}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => setShowContactModal(false)}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingContact || !contactForm.displayName}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-bold transition-colors disabled:opacity-50"
              style={{ backgroundColor: "var(--color-primary)" }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "var(--color-primary)")}
            >
              {creatingContact ? "Creating..." : "Create Contact"}
            </button>
          </div>
        </form>
      </PremiumModal>

      {/* ── Import Modal ── */}
      <DataImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImported={() => fetchPeople()}
        defaultEntityType={activeTab === "contacts" ? "contacts" : "clients"}
      />
    </div>
  );
};

export default PeoplePage;
